import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2, AlertCircle } from "lucide-react";

const fieldCls = "w-full rounded-md border border-gray-200 px-3 py-2 text-sm";

export default function ExpenseImportPage() {
    const [sites, setSites] = useState([]);
    const [siteId, setSiteId] = useState("");
    const [defaultMonth, setDefaultMonth] = useState("");
    const [preview, setPreview] = useState(null);
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => { api.get("/sites").then(({ data }) => setSites(data)); }, []);

    async function doPreview() {
        if (!siteId || !file) return toast.error("Site + file required");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("site_id", siteId);
            if (defaultMonth) fd.append("default_month", defaultMonth);
            fd.append("file", file);
            const { data } = await api.post("/expenses/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setPreview(data);
            setResult(null);
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    }

    async function doCommit() {
        if (!preview) return;
        setBusy(true);
        try {
            const rows = preview.rows.filter((r) => r.amount > 0).map((r) => ({
                date: r.date, vendor_name: r.vendor_name, invoice_number: r.invoice_number,
                invoice_date: r.invoice_date, category_name: r.category_name, description: r.description,
                quantity: r.quantity, unit: r.unit, amount: r.amount, remarks: r.remarks,
            }));
            const { data } = await api.post("/expenses/import/commit", { site_id: preview.site_id, rows });
            setResult(data);
            toast.success(`Imported ${data.inserted} rows`);
            setPreview(null); setFile(null);
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    }

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-8">
            <div className="text-xs uppercase tracking-wider text-gray-500">Admin</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Historical CSV import</h1>
            <p className="text-sm text-gray-500 mt-1">
                Upload a CSV to import historical expenses. Columns recognised: <code className="text-xs">Date, Vendor Name, Invoice No, Invoice Date, Category, Description, Quantity, Unit, Amount, Remarks</code>. Unknown vendors are created as <strong>unverified</strong>; unknown categories fall back to “Other”. Imported rows are marked <code className="text-xs">source=import</code> and skip the invoice-pending nag.
            </p>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        <div className="text-xs text-gray-500 mb-1">Site</div>
                        <select value={siteId} onChange={(e) => setSiteId(e.target.value)} className={fieldCls} data-testid="import-site">
                            <option value="">— select site —</option>
                            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 mb-1">Default month (optional)</div>
                        <input type="month" value={defaultMonth} onChange={(e) => setDefaultMonth(e.target.value)} className={fieldCls} data-testid="import-default-month" />
                    </div>
                    <div>
                        <div className="text-xs text-gray-500 mb-1">CSV file</div>
                        <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-sm" data-testid="import-file" />
                    </div>
                </div>
                <div className="flex justify-end">
                    <button onClick={doPreview} disabled={busy || !siteId || !file} className="rounded-md bg-[#1E3355] text-white px-4 py-2 text-sm font-medium disabled:opacity-60" data-testid="import-preview-btn">
                        <Upload className="inline w-4 h-4 mr-1" />Preview
                    </button>
                </div>
            </div>

            {preview && (
                <div className="mt-6 rounded-xl bg-white border border-gray-100">
                    <div className="px-4 py-3 border-b flex items-center justify-between">
                        <div className="font-bold text-[#1E3355]">Preview — {preview.count} rows</div>
                        <button onClick={doCommit} disabled={busy} className="rounded-md bg-emerald-600 text-white px-4 py-2 text-sm font-medium disabled:opacity-60" data-testid="import-commit-btn">
                            Commit import
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead className="bg-[#F4F6F9] text-xs uppercase tracking-wider text-gray-500">
                                <tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Vendor</th><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-left">Description</th><th className="px-3 py-2 text-left">Warnings</th></tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {preview.rows.map((r) => (
                                    <tr key={r.row_number} data-testid={`preview-row-${r.row_number}`}>
                                        <td className="px-3 py-2 text-xs">{r.row_number}</td>
                                        <td className="px-3 py-2">{r.date}</td>
                                        <td className="px-3 py-2">{r.vendor_name}{!r.vendor_id && <AlertCircle className="w-3 h-3 inline ml-1 text-amber-600" />}</td>
                                        <td className="px-3 py-2">{r.category_name}{!r.category_id && <AlertCircle className="w-3 h-3 inline ml-1 text-amber-600" />}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{Number(r.amount).toLocaleString("en-IN")}</td>
                                        <td className="px-3 py-2 max-w-[240px] truncate">{r.description}</td>
                                        <td className="px-3 py-2 text-xs text-amber-700">{r.warnings.join(" · ")}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {result && (
                <div className="mt-4 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="import-result">
                    <CheckCircle2 className="w-4 h-4" /> Inserted {result.inserted}; skipped {result.skipped}.
                </div>
            )}
        </div>
    );
}
